import { buildFunnel, stageCounts, summarizeLeadStages } from '../logic/leadStages';
import { shell } from '../app';
import { getSnapshot, onStoreChange } from '../data/store';
import { inPersonOnly } from '../logic/activity';
import { leadsCsv, visitsCsv } from '../logic/csv';
import {
  contractsExpiring,
  topObjections,
  visitsPerDay,
  visitsPerWeek,
} from '../logic/dashboard';
import { formatDate, parseIsoDate, todayLocal } from '../logic/dates';
import { btn, empty, section } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { exportFile } from '../ui/download';
import { OBJECTION_LABELS, STAGE_LABELS } from '../ui/labels';
import type { View } from '../ui/router';

/** Reports (M5, D36; renamed from Stats in D56): history, funnel, objections and the Salesforce-ready
 *  CSV export. The week-to-date numbers moved to the Home. All from the mirror. */

const pct = (n: number): string => `${Math.round(n * 100)}%`;

/** The buckets are exclusive (0–30, 31–60, 61–90), named as on the Home card, not "≤ 60". */
const CONTRACT_BUCKET_LABELS = { 30: 'within 30 days', 60: '31 – 60 days', 90: '61 – 90 days' } as const;

export const dashboardView: View = ({ root, query }) => {
  shell.setChrome(true);
  shell.setTitle('Reports', {
    back: query.get('from') === 'pipeline' ? '#/pipeline' : '#/home',
  });
  const host = h('div', { class: 'page page--dashboard' });
  root.appendChild(host);

  let seq = 0;
  const render = async () => {
    const run = ++seq;
    const today = todayLocal();
    const snap = await getSnapshot();
    if (run !== seq) return; // a newer render is on its way: an older snapshot must not paint last
    // "Visits" means visits: calls, emails and voicemails live on the Activity screen (D90)
    const inPerson = inPersonOnly(snap.visits);
    const days = visitsPerDay(inPerson, today, 14);
    const weeks = visitsPerWeek(inPerson, today, 8);
    const funnel = buildFunnel(snap.leads);
    const objections = topObjections(snap.objections, 5);
    const stages = stageCounts(snap.leads);
    const summary = summarizeLeadStages(snap.leads);
    const contracts = contractsExpiring(snap.leads, today);
    const dayMax = Math.max(1, ...days.map((d) => d.count));
    const weekMax = Math.max(1, ...weeks.map((w) => w.count));
    const funnelMax = Math.max(1, funnel[0]?.reached ?? 1);
    const objMax = Math.max(1, ...objections.map((o) => o.count));
    const last14 = days.reduce((n, d) => n + d.count, 0);

    replaceChildren(
      host,
      section(
        { title: 'Visits · last 14 days', count: last14 },
        h(
          'div',
          { class: 'card colchart', role: 'img', 'aria-label': `${last14} visits in the last 14 days` },
          days.map((d) =>
            h(
              'div',
              { class: 'colchart__col', title: `${formatDate(d.date, today)}: ${d.count}` },
              span('colchart__count mono', d.count > 0 ? String(d.count) : ''),
              h('div', {
                class: `colchart__bar${d.date === today ? ' colchart__bar--today' : ''}`,
                style: { height: `${Math.max(4, (d.count / dayMax) * 100)}%` },
              }),
              span('colchart__label mono', String(parseIsoDate(d.date).getDate())),
            ),
          ),
        ),
      ),
      section(
        { title: 'Visits · last 8 weeks' },
        h(
          'div',
          { class: 'card' },
          weeks.map((w) =>
            h(
              'div',
              { class: 'hbar' },
              span('hbar__label mono', `wk ${formatDate(w.start, today)}`),
              h(
                'div',
                { class: 'hbar__track' },
                h('div', { class: 'hbar__fill', style: { width: `${(w.count / weekMax) * 100}%` } }),
              ),
              span('hbar__value mono', String(w.count)),
            ),
          ),
        ),
      ),
      section(
        { title: 'Funnel · leads at or past each stage' },
        h(
          'div',
          { class: 'card' },
          funnel.map((r, i) =>
            h(
              'div',
              { class: 'hbar' },
              span('hbar__label mono', `${STAGE_LABELS[r.stage]}${r.stage === 'closed_won' ? '' : ' or later'}`),
              h(
                'div',
                { class: 'hbar__track' },
                h('div', {
                  class: 'hbar__fill hbar__fill--funnel',
                  style: { width: `${(r.reached / funnelMax) * 100}%` },
                }),
              ),
              span('hbar__value mono', `${r.reached}${i > 0 ? ` · ${pct(r.conversion)}` : ''}`),
            ),
          ),
          h(
            'p',
            { class: 'muted small chart__note' },
            'Current stages, not historical progress. Excludes Lost; % is versus the previous rung.',
          ),
        ),
      ),
      section(
        { title: 'Top objections' },
        objections.length
          ? h(
              'div',
              { class: 'card' },
              objections.map((o) =>
                h(
                  'div',
                  { class: 'hbar' },
                  span('hbar__label mono', OBJECTION_LABELS[o.type]),
                  h(
                    'div',
                    { class: 'hbar__track' },
                    h('div', {
                      class: 'hbar__fill hbar__fill--warn',
                      style: { width: `${(o.count / objMax) * 100}%` },
                    }),
                  ),
                  span('hbar__value mono', String(o.count)),
                ),
              ),
            )
          : empty('No objections logged yet.'),
      ),
      section(
        { title: 'All leads · current stage', count: summary.all },
        h(
          'div',
          { class: 'card stagegrid' },
          stages.map((s) =>
            h(
              'div',
              { class: 'stagegrid__cell' },
              span('stagegrid__count mono', String(s.count)),
              span('stagegrid__label', STAGE_LABELS[s.stage]),
            ),
          ),
        ),
      ),
      section(
        { title: 'Contracts expiring · 90 days', count: contracts.total },
        h(
          'div',
          { class: 'card stagegrid stagegrid--3' },
          ([30, 60, 90] as const).map((b) =>
            h(
              'div',
              { class: 'stagegrid__cell' },
              span('stagegrid__count mono', String(contracts.byBucket[b])),
              span('stagegrid__label', CONTRACT_BUCKET_LABELS[b]),
            ),
          ),
        ),
      ),
      section(
        { title: 'Export · for Salesforce' },
        h(
          'div',
          { class: 'card stack-h' },
          btn(`All leads CSV (${summary.all})`, {
            variant: 'primary',
            size: 'sm',
            onClick: () => void exportFile(`oc-leads-${today}.csv`, leadsCsv(snap.leads)),
          }),
          btn(`Visits CSV (${snap.visits.length})`, {
            variant: 'subtle',
            size: 'sm',
            onClick: () => void exportFile(`oc-visits-${today}.csv`, visitsCsv(snap.visits, snap.leads)),
          }),
        ),
        h(
          'p',
          { class: 'muted small chart__note' },
          'Generated on this phone from local data — nothing leaves the device until you share the file.',
        ),
      ),
    );
  };

  void render();
  return onStoreChange(() => void render()); // a sync landing while Reports is open shows up
};
